/**
 * components/landing/home/Backtesting.jsx — Section 7.
 *
 * A realistic backtest report preview: equity curve, drawdown, key
 * metrics, trade distribution, monthly returns heatmap and a risk
 * analysis panel.  Everything is DOM+SVG for perf.
 */

import { Container, SectionHeading, Eyebrow } from '../design-system'
import { MockDashboardChrome, MiniSparkline, seededWalk } from './_shared'
import clsx from 'clsx'

const MONTHS = ['J','F','M','A','M','J','J','A','S','O','N','D']

const Backtesting = () => {
  const equity   = seededWalk(21, 140, 4, 100)
  const drawdown = equity.map((v, i) => Math.min(0, v - Math.max(...equity.slice(0, i + 1))))
  const heatmap = Array.from({ length: 4 * 12 }, (_, i) => {
    const seed = (i * 9301 + 4929) % 233280
    return ((seed / 233280) - 0.5) * 10  // -5% .. +5%
  })
  const distribution = Array.from({ length: 18 }, (_, i) => {
    const bucket = i - 8
    return Math.max(1, Math.round(15 * Math.exp(-Math.pow(bucket, 2) / 8)) + ((i * 137) % 5))
  })

  return (
    <section className="py-24 md:py-40 bg-[radial-gradient(60%_40%_at_50%_0%,rgba(16,185,129,0.05),transparent)]">
      <Container>
        <SectionHeading
          eyebrow="Backtesting"
          title={<>Reports designed for <span className="text-brand-emerald">real research decisions</span>.</>}
          subtitle="Not just an equity curve.  Kalki Ai Cloud ships every metric a serious quant expects, out of the box."
          align="center"
          className="items-center mx-auto"
        />

        <div className="mt-14">
          <MockDashboardChrome className="max-w-6xl mx-auto" innerClass="p-5 min-h-[520px]">
            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {[['Return','+38.4%','green'],['Win-rate','61.4%','green'],['Profit factor','1.83','green'],['Max DD','-6.4%','red'],['Sharpe','1.42','green'],['Trades','142','neutral']].map(([k,v,t]) => (
                <div key={k} className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</div>
                  <div className={clsx('text-lg font-semibold tabular-nums mt-0.5', t === 'green' ? 'text-brand-emerald' : t === 'red' ? 'text-red-400' : 'text-white')}>{v}</div>
                </div>
              ))}
            </div>

            {/* Chart grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              {/* Equity + drawdown */}
              <div className="md:col-span-2 space-y-3">
                <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Equity curve</div>
                    <div className="text-[10px] text-brand-emerald font-mono">+38.4%</div>
                  </div>
                  <MiniSparkline data={equity} stroke="#10B981" height={140} width={640} className="w-full" />
                </div>
                <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Drawdown</div>
                  <MiniSparkline data={drawdown} stroke="#ef4444" filled height={80} width={640} className="w-full" />
                </div>
              </div>

              {/* Trade distribution */}
              <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Trade distribution (R-multiple)</div>
                <div className="flex items-end justify-between gap-0.5 h-40">
                  {distribution.map((v, i) => (
                    <div key={i} className={clsx('flex-1 rounded-t', i < 8 ? 'bg-red-400/60' : i === 8 ? 'bg-zinc-500/70' : 'bg-brand-emerald/70')} style={{ height: `${(v / Math.max(...distribution)) * 100}%` }} />
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between text-[9px] text-zinc-500 font-mono"><span>-4R</span><span>0R</span><span>+4R</span></div>
              </div>
            </div>

            {/* Monthly returns + risk */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div className="md:col-span-2 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Monthly returns</div>
                  <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-red-400/70" />loss</span>
                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-brand-emerald/70" />gain</span>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-1">
                  {heatmap.map((v, i) => {
                    const intensity = Math.min(1, Math.abs(v) / 5)
                    const bg = v >= 0 ? `rgba(0,200,83,${0.2 + intensity * 0.6})` : `rgba(239,68,68,${0.2 + intensity * 0.6})`
                    return <div key={i} className="aspect-square rounded" style={{ backgroundColor: bg }} title={`${v.toFixed(2)}%`} />
                  })}
                </div>
                <div className="mt-2 flex items-center justify-between text-[9px] text-zinc-500 font-mono">
                  {MONTHS.map((m, mi) => <span key={mi}>{m}</span>)}
                </div>
              </div>
              <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Risk analysis</div>
                {[['VaR 95%','-2.8%'],['CVaR','-4.1%'],['Kelly','0.24'],['MFE / MAE','1.9 / 1.2'],['Trade avg','+0.42R'],['Consecutive losses','3 max']].map(([k,v]) => (
                  <div key={k} className="flex items-center justify-between text-[11px]">
                    <span className="text-zinc-500">{k}</span><span className="text-white font-mono">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </MockDashboardChrome>
        </div>
      </Container>
    </section>
  )
}

export default Backtesting
